import queryString from 'query-string';
import React, {useLayoutEffect} from 'react';
import {Redirect} from 'react-router-dom';

import {SignupButton} from '../../components/LoginButton';
import {AccountType} from '../../components/Search/SearchNav/types';
import {InstrumentedLoader as Loader} from '../../components/utility/InstrumentedLoader';
import config, {envIsProd} from '../../config';
import {withViewer} from '../../graphql/users_get_viewer';
import {auth} from '../../setup';
import {type User} from '../../types/graphql';
import {safeLocalStorage} from '../../util/localStorage';
import {forceRedirect} from '../../util/navigation';
import * as urls from '../../util/urls';

const REDIRECT_TO_SITE = !config.ENVIRONMENT_IS_PRIVATE && envIsProd;
interface HomePageRouterProps {
  viewerLoading: boolean;
  viewer?: User;
}

// Export Home for testing purposes
export const HomePageRouter: React.FC<HomePageRouterProps> = ({
  viewerLoading,
  viewer,
}) => {
  const params = queryString.parse(document.location.search);
  const fromVerifyEmail = 'verifyEmail' in params;

  useLayoutEffect(() => {
    if (
      !REDIRECT_TO_SITE ||
      params.loader ||
      viewerLoading ||
      viewer != null ||
      fromVerifyEmail
    ) {
      return;
    }
    forceRedirect('/site');
  }, [fromVerifyEmail, params.loader, viewer, viewerLoading]);

  if (params.loader) {
    let backgroundColor = '#ecbb33';
    if (params.color != null) {
      backgroundColor =
        typeof params.color === 'string' ? params.color : params.color[0];
    }
    return (
      <div
        style={{
          backgroundColor,
          width: '100%',
          height: 500,
          position: 'relative',
        }}>
        <Loader name="home-page-params-loader" {...params} />
      </div>
    );
  }
  if (viewerLoading) {
    return <Loader name="home-page-viewer-loader" />;
  }
  if (fromVerifyEmail && viewer != null) {
    const isAwaitingWorkEmailVerificationFromOnboarding =
      safeLocalStorage.getItem('usageTypeAwaitingVerification') ===
      AccountType.Corporate;
    if (isAwaitingWorkEmailVerificationFromOnboarding) {
      return (
        <Redirect to={{pathname: urls.signup(), state: {internal: true}}} />
      );
    }
  }
  if (viewer != null) {
    // Directly go to our logged in home page view
    return (
      <Redirect to={{pathname: urls.homePage(), state: {internal: true}}} />
    );
  }
  // Immediately log the user in if they're redirected from email verification link
  if (fromVerifyEmail) {
    auth.login();
    return <Loader name="home-page-verify-loader" />;
  }

  if (!REDIRECT_TO_SITE) {
    /* eslint-disable wandb/no-a-tags */
    return (
      <div className="front-page">
        <div className="hero">
          <h1>Developer tools for AI</h1>
          <h2>Those who don't track their work are doomed to redo it.</h2>
          {!config.ENVIRONMENT_IS_PRIVATE && <SignupButton hideIcon={true} />}
        </div>
      </div>
    );
    /* eslint-enable wandb/no-a-tags */
  }
  return null;
};

export default withViewer(HomePageRouter);
